import Papa from "papaparse";
import { ValidationError } from "./types";

export interface ParsedArtistRow {
  /**
   * The row's line number in the uploaded CSV (header is line 1, so the first
   * data row is 2). ValidationError.row uses the same numbering.
   *
   * Needed because `validRows` is compacted — rows that failed validation are
   * not pushed — so a row's position in this array stops matching its position
   * in the file as soon as anything is dropped. Consumers must match errors on
   * this field, never on array index.
   */
  rowNumber: number;
  artistName: string;
  spotifyURI: string;
  appleID: string;
  asin: string;
  soundcloudURL: string;
  tidalURL: string;
  photo: string;
  bio: string;
  hometown: string;
  currentTown: string;
  country: string;
  label: string;
  distributor: string;
  instagram: string;
  tiktok: string;
  twitter: string;
  facebook: string;
  snapchat: string;
  spotifyURL: string;
  appleURL: string;
  amazonURL: string;
  deezerURL: string;
}

export interface ParseResult {
  validRows: ParsedArtistRow[];
  errors: ValidationError[];
}

const SPOTIFY_ID_PATTERN = /^[a-zA-Z0-9]{22}$/;
const SPOTIFY_URI_PATTERN = /^spotify:artist:[a-zA-Z0-9]{22}$/;
const APPLE_ID_PATTERN = /^\d+$/;
const ASIN_PATTERN = /^[a-zA-Z0-9]{10}$/;
const URL_PATTERN = /^(https?:\/\/[^\s$.?#].[^\s]*)$/;

const REQUIRED_FIELDS = [
  "Artist Name",
  "Spotify URI",
  "Apple Artist ID",
  "ASIN",
  "SoundCloud URL",
  "TIDAL URL",
] as const;

const REQUIRED_URL_FIELDS = ["SoundCloud URL", "TIDAL URL"] as const;

const OPTIONAL_URL_FIELDS = [
  "Instagram",
  "TikTok",
  "X",
  "Facebook",
  "Snapchat",
  "Spotify URL",
  "Apple URL",
  "Amazon URL",
  "Deezer URL",
] as const;

const normalizeHeader = (header: string): string =>
  header.trim().replace(/^﻿/, "").replace(/\*$/, "");

const extractSpotifyId = (value: string): string =>
  value.startsWith("spotify:artist:") ? value.replace("spotify:artist:", "") : value;

const cleanValue = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

export const parseAndValidateCSV = (csvText: string): ParseResult => {
  const validRows: ParsedArtistRow[] = [];
  const errors: ValidationError[] = [];

  const parsed = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: normalizeHeader,
  });

  if (parsed.errors?.length) {
    for (const err of parsed.errors) {
      errors.push({
        row: typeof err.row === "number" ? err.row + 2 : 0,
        field: "CSV Format",
        message: err.message,
        severity: "error",
      });
    }
  }

  const rows = parsed.data || [];

  rows.forEach((rawRow, idx) => {
    const rowNumber = idx + 2;
    const row: Record<string, string> = {};
    for (const key of Object.keys(rawRow)) {
      row[key] = cleanValue(rawRow[key]);
    }

    let hasErrors = false;

    const missingFields = REQUIRED_FIELDS.filter(
      (field) => !row[field] || row[field] === "",
    );
    if (missingFields.length > 0) {
      errors.push({
        row: rowNumber,
        field: "Required Fields",
        message: `Missing required fields: ${missingFields.join(", ")}`,
        severity: "error",
      });
      hasErrors = true;
    }

    if (row["Spotify URI"]) {
      const v = row["Spotify URI"];
      if (!SPOTIFY_ID_PATTERN.test(v) && !SPOTIFY_URI_PATTERN.test(v)) {
        errors.push({
          row: rowNumber,
          field: "Spotify URI",
          message:
            "Invalid Spotify URI format. Should be either full URI (spotify:artist:25BObiRSDCMwVrBGIVaLIf) or just ID (25BObiRSDCMwVrBGIVaLIf)",
          severity: "error",
        });
        hasErrors = true;
      }
    }

    if (row["Apple Artist ID"] && !APPLE_ID_PATTERN.test(row["Apple Artist ID"])) {
      errors.push({
        row: rowNumber,
        field: "Apple Artist ID",
        message: "Apple Artist ID must contain only numbers",
        severity: "error",
      });
      hasErrors = true;
    }

    if (row["ASIN"] && !ASIN_PATTERN.test(row["ASIN"])) {
      errors.push({
        row: rowNumber,
        field: "ASIN",
        message: "ASIN must be exactly 10 characters (letters and numbers only)",
        severity: "error",
      });
      hasErrors = true;
    }

    for (const field of REQUIRED_URL_FIELDS) {
      if (row[field] && !URL_PATTERN.test(row[field])) {
        errors.push({
          row: rowNumber,
          field,
          message: `Invalid URL format in ${field}`,
          severity: "error",
        });
        hasErrors = true;
      }
    }

    for (const field of OPTIONAL_URL_FIELDS) {
      if (row[field] && !URL_PATTERN.test(row[field])) {
        errors.push({
          row: rowNumber,
          field,
          message: `Invalid URL format in ${field}`,
          severity: "error",
        });
        hasErrors = true;
      }
    }

    if (!hasErrors) {
      validRows.push({
        rowNumber,
        artistName: row["Artist Name"],
        spotifyURI: extractSpotifyId(row["Spotify URI"]),
        appleID: row["Apple Artist ID"],
        asin: row["ASIN"],
        soundcloudURL: row["SoundCloud URL"],
        tidalURL: row["TIDAL URL"],
        photo: row["Photo"] || "",
        bio: row["Bio"] || "",
        hometown: row["Artist Hometown"] || "",
        currentTown: row["Artist Current Town"] || "",
        country: row["Country"] || "",
        label: row["Label"] || "",
        distributor: row["Distributor"] || "",
        instagram: row["Instagram"] || "",
        tiktok: row["TikTok"] || "",
        twitter: row["X"] || "",
        facebook: row["Facebook"] || "",
        snapchat: row["Snapchat"] || "",
        spotifyURL: row["Spotify URL"] || "",
        appleURL: row["Apple URL"] || "",
        amazonURL: row["Amazon URL"] || "",
        deezerURL: row["Deezer URL"] || "",
      });
    }
  });

  return { validRows, errors };
};
